<template>
  <div
    class="browse-filters"
    :class="{
      show: mobileOpen,
      'has-filters': filters.length > 0,
      'full-width': fullWidth }"
  >
    <div class="header">
      <a>
        <img
          :src="closeIcon"
          alt="Close"
          height="16"
          width="16"
          @click="$emit('close')"
        >
      </a>
      <h5>Filters</h5>
    </div>
    <div class="container">
      <browse-filter-attribute-categories
        :attributes="attributes"
        :group="group"
        :category="category"
        :subcategory="subcategory"
        :values="value.attributes"
        :is-searching="isSearching"
        @group="changeAttr('group', $event)"
        @category="changeAttr('category', $event)"
        @subcategory="changeAttr('subcategory', $event)"
      />
      <div
        v-for="filter in sortedFilters"
        :key="filter.key || filter.type"
        v-frag
      >
        <browse-filter-mobile-sort
          v-if="filter.type == 'sort'"
          :value="value.sort"
          @input="change('sort', $event)"
        />
        <browse-filter-select
          v-if="filter.type === 'marketplace' && filters.includes('marketplace')"
          :value="value.marketplace === false"
          @input="change('marketplace', $event)"
        />
        <browse-filter-black-friday
          v-if="filter.type === 'black-friday' && filters.includes('black-friday')"
          :value="[true, 'true'].includes(value.bf)"
          @input="change('bf', $event)"
        />
        <browse-filter-sumo-day
          v-if="filter.type === 'sumo-day' && filters.includes('sumo-day')"
          :value="[true, 'true'].includes(value.sumoday)"
          @input="change('sumoday', $event)"
        />
        <browse-filter-rating
          v-if="filter.type === 'average_rating' && filters.includes('average_rating')"
          :value="Number(value.average_rating) || null"
          @input="change('average_rating', $event)"
        />
        <browse-filter-plan-types
          v-if="filter.type === 'plan_types' && filters.includes('plan_types')"
          :value="value.plan_type"
          :plan-types="aggregations.plan_types"
          @input="change('plan_type', $event)"
        />
        <browse-filter-status
          v-if="filter.type === 'status' && filters.includes('status')"
          :value="value.status"
          :statuses="aggregations.status"
          @input="change('status', $event)"
        />
        <browse-filter-price-range
          v-if="filter.type === 'price_range' && filters.includes('price_range')"
          :value="value.price_range"
          @input="change('price_range', $event)"
        />
        <browse-filter-attribute-boolean
          v-if="filter.type === 'attribute' && filter.attribute.type === 'boolean'"
          :attribute="filter.attribute"
          :value="value.attributes[filter.attribute.slug] || false"
          @input="changeAttr(filter.attribute.slug, $event)"
        />
        <browse-filter-attribute-enumeration-single
          v-if="filter.type === 'attribute' && filter.attribute.type === 'enumeration'
            && filter.attribute.type_options.multiple !== true"
          :attribute="filter.attribute"
          :value="value.attributes[filter.attribute.slug] || undefined"
          :values="value.attributes"
          @input="changeAttr(filter.attribute.slug, $event)"
        />
        <browse-filter-attribute-enumeration-multiple
          v-if="filter.type === 'attribute' && filter.attribute.type === 'enumeration'
            && filter.attribute.type_options.multiple === true"
          :attribute="filter.attribute"
          :value="value.attributes[filter.attribute.slug] || []"
          :values="value.attributes"
          @input="changeAttr(filter.attribute.slug, $event)"
        />
        <browse-filter-attribute-tag
          v-if="filter.type === 'attribute' && filter.attribute.type === 'tag'"
          :attribute="filter.attribute"
          :bucket="filter"
          :value="value.attributes[filter.attribute.slug] || []"
          @input="changeAttr(filter.attribute.slug, $event)"
        />
      </div>
    </div>
    <div class="footer">
      <div>
        <button class="clear" @click="$emit('clear')">
          Clear filters
        </button>
      </div>
      <div>
        <button class="apply" @click="$emit('apply')">
          Apply <span v-if="mobileResults !== null">({{ mobileResults | number('0,0') }})</span>
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import cloneDeep from 'lodash/cloneDeep';
import moment from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import set from 'lodash/set';
import closeIcon from '@legacy/static/images/svg/close.svg';
import frag from 'vue-frag';
import {
  BLACK_FRIDAY_SLUG,
  SUMO_DAY_SLUG,
} from '@/constants/global';
import { filterAttributeDependencies, checkDependency } from '@/datalayer/helpers/attributes';
import CampaignsMixin from '~/mixins/campaigns';
import BrowseFilterAttributeCategories from './BrowseFilterAttributeCategories';
import BrowseFilterMobileSort from './BrowseFilterMobileSort';
import BrowseFilterSelect from './BrowseFilterSelect';
import BrowseFilterBlackFriday from './BrowseFilterBlackFriday';
import BrowseFilterSumoDay from './BrowseFilterSumoDay';
import BrowseFilterRating from './BrowseFilterRating';
import BrowseFilterPlanTypes from './BrowseFilterPlanTypes';
import BrowseFilterStatus from './BrowseFilterStatus';
import BrowseFilterPriceRange from './BrowseFilterPriceRange';
import BrowseFilterAttributeBoolean from './BrowseFilterAttributeBoolean';
import BrowseFilterAttributeEnumerationSingle from './BrowseFilterAttributeEnumerationSingle';
import BrowseFilterAttributeEnumerationMultiple from './BrowseFilterAttributeEnumerationMultiple';
import BrowseFilterAttributeTag from './BrowseFilterAttributeTag';

moment.extend(isBetween);

export default {
  directives: { frag },
  components: {
    BrowseFilterAttributeCategories,
    BrowseFilterMobileSort,
    BrowseFilterSelect,
    BrowseFilterBlackFriday,
    BrowseFilterSumoDay,
    BrowseFilterRating,
    BrowseFilterPlanTypes,
    BrowseFilterStatus,
    BrowseFilterPriceRange,
    BrowseFilterAttributeBoolean,
    BrowseFilterAttributeEnumerationSingle,
    BrowseFilterAttributeEnumerationMultiple,
    BrowseFilterAttributeTag,
  },
  mixins: [CampaignsMixin],
  props: {
    isSearching: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      required: true,
    },
    smartCollectionFilters: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    aggregations: {
      type: Object,
      required: true,
    },
    mobileOpen: {
      type: Boolean,
      required: false,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    mobileResults: {
      type: Number,
      required: false,
      default: null,
    },
    isWishlist: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      closeIcon,
    };
  },
  computed: {
    profileDefaultKeys() {
      return this.smartCollectionFilters?.filters || [];
    },
    profileAttributeKeys() {
      return this.smartCollectionFilters?.attributes || [];
    },
    group() {
      const attribs = (
        this.isSearching ? this.aggregations?.attributes : this.$store.state.browse.attributes)
        || [];
      return attribs.find((a) => a.slug === 'group');
    },
    category() {
      const attribs = (
        this.isSearching ? this.aggregations?.attributes : this.$store.state.browse.attributes)
        || [];
      return attribs.find((a) => a.slug === 'category');
    },
    subcategory() {
      const attribs = (
        this.isSearching ? this.aggregations?.attributes : this.$store.state.browse.attributes)
        || [];
      return attribs.find((a) => a.slug === 'subcategory');
    },
    attributes() {
      /*
      let attributes = this.$store.state.browse.attributes.map((a) => ({
        attribute: a,
      }));

      attributes = attributes.filter((a) => a.attribute.can_filter === true);
      attributes = attributes.filter((a) => checkDependency(
        a.attribute.dependency, this.value.attributes,
      ));

      attributes.sort((a, b) => {
        const order = ['group', 'category', 'subcategory', 'product_type'];
        const aPos = order.indexOf(a.attribute.slug);
        const bPos = order.indexOf(b.attribute.slug);
        return aPos - bPos;
      });
      */
      const attributes = [...(this.aggregations?.attributes || [])];
      attributes.sort((a, b) => {
        if (a.doc_count === b.doc_count) {
          return a.name.localeCompare(b.name);
        }

        return b.doc_count - a.doc_count;
      });

      return attributes.filter((a) => {
        if (!checkDependency(a.dependency, this.value.attributes)) {
          return false;
        }

        return true;
      });
    },
    sortedFilters() {
      const filters = [
        {
          type: 'sort',
          priority: Number.MAX_VALUE,
        },
        {
          type: 'marketplace',
          priority: 9e3,
        },
        {
          type: 'black-friday',
          priority: 8e3,
        },
        {
          type: 'sumo-day',
          priority: 8e3,
        },
        {
          type: 'tags',
          priority: 7e3,
        },
        /*
        {
          type: 'average_rating',
          priority: 6e3,
        },
        */
        {
          type: 'plan_types',
          priority: 5e3,
        },
        {
          type: 'status',
          priority: 4e3,
        },
        {
          type: 'price_range',
          priority: 3e3,
        },
      ];

      const excludeFilters = [
        'group', 'category', 'subcategory',
        ...this.profileAttributeKeys,
      ];
      this.attributes.forEach((attr) => {
        if (!excludeFilters.includes(attr.slug)) {
          filters.push({
            key: attr.slug,
            type: 'attribute',
            priority: attr.weight,
            attribute: attr,
          });
        }
      });

      filters.sort((a, b) => b.priority - a.priority);

      return filters;
    },
    isBlackFridayCampaign() {
      return this.isCampaignActive(BLACK_FRIDAY_SLUG);
    },
    isSumoDayCampaign() {
      return this.isCampaignActive(SUMO_DAY_SLUG);
    },
    filters() {
      const filters = [];
      const keys = this.profileDefaultKeys;

      if (!keys.includes('marketplace')) {
        const marketplace = this.aggregations?.marketplace;
        let hasSelect = true;

        if (marketplace) {
          const select = marketplace.find((a) => Number(a.key) === 0);

          if (select) {
            hasSelect = (Number(select.doc_count) > 0);
          }
        }

        if (hasSelect) {
          filters.push('marketplace');
        }
      }

      if (!keys.includes('tags')) {
        filters.push('tags');
      }

      if (!keys.includes('average_rating')) {
        filters.push('average_rating');
      }

      if (!keys.includes('type')) {
        filters.push('plan_types');
      }

      if (!keys.includes('status') || this.isWishlist) {
        filters.push('status');
      }

      if (!keys.includes('default_price')) {
        filters.push('price_range');
      }

      if (!keys.includes('bf') && this.isBlackFridayCampaign) {
        filters.push('black-friday');
      }

      if (!keys.includes('sumoday') && this.isSumoDayCampaign) {
        filters.push('sumo-day');
      }

      return filters;
    },
  },
  methods: {
    change(key, value) {
      // deep clone
      const filters = cloneDeep(this.value);
      set(filters, key, value);
      this.$emit('input', filters);
    },
    changeAttr(key, value) {
      let attributes = { ...this.value.attributes };
      if (typeof value !== 'undefined') {
        attributes[key] = value;
      } else {
        delete attributes[key];
      }

      // when changing category, reset any subcategory too
      if (key === 'group') {
        delete attributes.category;
        delete attributes.subcategory;
      } else if (key === 'category') {
        delete attributes.subcategory;
      }
      attributes = filterAttributeDependencies(this.$store.state.browse.attributes, attributes);
      this.change('attributes', attributes);
    },
  },
};
</script>
<style scoped>
.browse-filters {
  width: 300px;
  margin-right: 20px;
  flex-direction: column;
}

.browse-filters ::v-deep input[type=radio],
.browse-filters ::v-deep input[type=checkbox] {
  width: 0;
  height: 0;
  opacity: 0;
}

.browse-filters:not(.has-filters) {
  display: none;
}

.browse-filters .container {
  padding: 0;
}

.header {
  padding: 16px;
  display: none;
}

.header a {
  width: 16px;
  height: 16px;
  display: block;
  position: absolute;
  top: 20px;
  right: 20px;
}

.header h5 {
  font-family: Barlow, sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 24px;
  margin: 32px 0 0;
  color: #1b1b1b;
}

.footer {
  display: none;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  padding: 16px;
}

.footer div {
  flex: 1 1 0;
}

.footer div:last-child {
  text-align: right;
}

.footer button {
  border: 0;
  background: none;
  outline: none;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  padding: 8px 24px;
  border-radius: 40px;
}

.footer .clear {
  color: #0044c0;
  border: 1px solid #0044c0;
}

.footer .apply {
  color: #1b1b1b;
  background: #ffbc00;
}

@media (min-width: 768px) {
  .browse-filters.full-width {
    margin: 0;
    position: fixed;
    top: 0;
    right: -400px;
    bottom: 0;
    background: #fff;
    z-index: 9999;
    display: flex;
    width: 387px;
    max-width: 100%;
    box-shadow: -5px 0 5px 1px rgba(0, 0, 0, 0.25);
    transition: right 0.3s ease-in-out;
  }

  .browse-filters.full-width.show {
    right: 0;
  }

  .browse-filters.full-width .container {
    overflow-y: scroll;
    flex-grow: 1;
    flex-shrink: 1;
    padding: 16px;
  }

  .browse-filters.full-width .header {
    display: block;
  }

  .browse-filters.full-width .footer {
    display: flex;
  }
}

@media (max-width: 767px) {
  .browse-filters {
    margin: 0;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff;
    z-index: 9999;
    display: none;
  }

  .browse-filters.show {
    display: flex;
  }

  .browse-filters .container {
    overflow-y: scroll;
    flex-grow: 1;
    flex-shrink: 1;
    padding: 16px;
  }

  .header {
    display: block;
  }

  .footer {
    display: flex;
  }
}
</style>
